"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";

import {
  CurrencyInput,
  LabelCell,
  NumberDisplayCell,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  netSellingPrice: z.number().positive(),
  contributionMarginPerUnit: z.number().positive(),
  unitsSold: z.number().positive(),
  targetOperatingProfitPercentage: z.number().positive(),
});

const defaultValues = {
  netSellingPrice: 100,
  contributionMarginPerUnit: 42,
  unitsSold: 10_000,
  targetOperatingProfitPercentage: 20,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

interface ContentProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  totalRevenue: number;
  totalContributionMargin: number;
  fixedOperatingCost: number;
  targetOperatingProfit: number;
  t: ReturnType<typeof useTranslations<"Calculators">>;
  tCommon: ReturnType<typeof useTranslations<"Common">>;
}

export function OpexCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const totalRevenue = form.watch("unitsSold") * form.watch("netSellingPrice");
  const totalContributionMargin =
    form.watch("unitsSold") * form.watch("contributionMarginPerUnit");
  const targetOperatingProfit =
    (form.watch("targetOperatingProfitPercentage") / 100) * totalRevenue;
  const fixedOperatingCost = totalContributionMargin - targetOperatingProfit;

  const tableProps = {
    form,
    totalRevenue,
    totalContributionMargin,
    targetOperatingProfit,
    fixedOperatingCost,
    t,
    tCommon,
  } satisfies ContentProps;

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("targetOpex")}
          </p>
        </TableHeader>
        <DesktopTable {...tableProps} />
      </form>
    </Form>
  );
}

function DesktopTable({
  totalContributionMargin,
  targetOperatingProfit,
  fixedOperatingCost,
  totalRevenue,
  form,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table>
      <colgroup>
        <col className="w-3/4" />
        <col className="w-1/4" />
      </colgroup>
      <tbody className="bg-surface">
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="contributionMarginPerUnit"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>
                    {t("contributionMarginPerUnit")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="unitsSold"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell>
                  <UnstyledFormLabel>{t("unitsSold")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell>{t("totalRevenue")}</LabelCell>
          <NumberDisplayCell>{totalRevenue}</NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell>{t("totalContributionMargin")}</LabelCell>
          <NumberDisplayCell>{totalContributionMargin}</NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell className="font-bold">
            {t("fixedOperatingExpenses")}
          </LabelCell>
          <NumberDisplayCell className="font-bold">
            {fixedOperatingCost}
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell>{t("targetOperatingProfitDollar")}</LabelCell>
          <NumberDisplayCell>{targetOperatingProfit}</NumberDisplayCell>
        </Tr>

        <FormField
          control={form.control}
          name="targetOperatingProfitPercentage"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell className="font-bold">
                  <UnstyledFormLabel>
                    {t("targetOperatingProfitPercent")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} className="font-bold" />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0 md:border-x-0">
          <td colSpan={3} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={() => {
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}
